<script lang="ts">
  import debounce from "lodash.debounce";
  import { ChevronDown } from "lucide-svelte";

  import { formatBytes } from "$lib/format";
  import { makeQueryParams } from "$lib/query-params";
  import useSWR from "$lib/swr";
  import { makeErrorToast } from "$lib/toast";
  import Sparkline from "$lib/ui/charts/Sparkline.svelte";
  import StackedTimeseriesChart from "$lib/ui/charts/StackedTimeseriesChart.svelte";
  import MetricGraphContainer from "$lib/ui/dashboard/deployments/MetricGraphContainer.svelte";
  import MetricGraphNoData from "$lib/ui/dashboard/deployments/MetricGraphNoData.svelte";
  import Divider from "$lib/ui/system/Divider.svelte";
  import DropdownSelect from "$lib/ui/system/DropdownSelect.svelte";
  import DropdownSelectItem from "$lib/ui/system/DropdownSelectItem.svelte";
  import MinimalTable from "$lib/ui/system/MinimalTable.svelte";
  import MetricGraphSkeleton from "../deployments/MetricGraphSkeleton.svelte";
  import MetricsRangeDropdown from "../deployments/MetricsRangeDropdown.svelte";
  import { calculateBucketSize } from "../deployments/app-dashboard-store";
  import { getXDomain } from "../deployments/metrics";
  import { AppState } from "~proto/modal_proto/api";
  import type {
    AppInfo,
    WorkspaceActiveResources,
    WorkspaceMetric,
    WorkspaceMetricsResponse,
  } from "~proto/modal_server/proto/public_api";

  export let workspace: string;
  export let resources: WorkspaceActiveResources;
  export let timeBucketSecs: number = 90;

  const { data: workspaceMetrics, update } = useSWR<WorkspaceMetricsResponse>();
  let cachedWorkspaceMetrics: WorkspaceMetricsResponse | undefined;
  $: if ($workspaceMetrics) {
    cachedWorkspaceMetrics = $workspaceMetrics;
    processData($workspaceMetrics);
    xDomain = getXDomain(duration, timeRange);
  }
  const debouncedUpdate = debounce(update, 300, { leading: true });

  const {
    data: workspaceResourceTotals,
    update: updateWorkspaceResourceTotals,
    error: workspaceResourceTotalsError,
  } = useSWR<WorkspaceMetricsResponse>();

  $: if ($workspaceResourceTotalsError) {
    makeErrorToast($workspaceResourceTotalsError);
  }
  const debouncedUpdateWorkspaceResourceTotals = debounce(
    updateWorkspaceResourceTotals,
    300,
    { leading: true },
  );

  let end: number = new Date().getTime() / 1000;
  let start: number = end - 86400;
  let timeRange: [number, number] | undefined;
  let selectIndex: number | undefined;
  let live: boolean = true;
  let duration: number;
  let xDomain: [Date, Date];

  $: if (start && end) {
    duration = end - start;
    timeRange = [start, end];
    timeBucketSecs = calculateBucketSize(duration) ?? timeBucketSecs;
  }

  // List of brand colors for the chart regions
  const colors = [
    "#ED94A7",
    "#07B976",
    "#FFEA71",
    "#4AD7DD",
    "#F3734A",
    "#BCBCC2",
  ];

  $: seriesColors = colors.slice(0, appIds.length).reverse();

  const gpuOptions = Object.freeze({
    t4: {
      name: "T4 GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    a10g: {
      name: "A10 GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    a100: {
      name: "A100 GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    a100_80gb: {
      name: "A100 80GB GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    l4: {
      name: "L4 GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    h100: {
      name: "H100 GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    l40s: {
      name: "L40S GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
  } as const);

  const resourceOptions = Object.freeze({
    elapsed_ns: {
      name: "Containers",
      formatLabel: (d: number) => `${d.toFixed(2)} containers`,
      multiplier: 1e-9,
    },
    cpu_ns: {
      name: "CPU cores",
      formatLabel: (d: number) => `${d.toFixed(2)} cores`,
      multiplier: 1e-9,
    },
    mem_ns: {
      name: "Memory",
      formatLabel: (d: number) => formatBytes(d * (1 << 30), { decimals: 2 }),
      multiplier: 1e-9,
    },
    gpu_ns: {
      name: "All GPUs",
      formatLabel: (d: number) => `${d.toFixed(2)} GPUs`,
      multiplier: 1e-9,
    },
    ...gpuOptions,
  } as const);

  type Resource = keyof typeof resourceOptions;

  let selectedResource: Resource = "elapsed_ns";
  let hoveredIndex: number | undefined;
  let hoveredTotalResources:
    | Pick<
        WorkspaceActiveResources,
        "activeGpus" | "activeContainers" | "gpuCounts"
      >
    | undefined;

  function getHoveredTotalResources(
    processedData: ProcessedData[],
    hoveredIndex: number | undefined,
  ) {
    if (hoveredIndex === undefined) {
      return;
    }
    // processedData[hoveredIndex] may be undefined after zooming in, before the data is updated
    const hoveredTimestampMs = processedData[hoveredIndex]?.date.getTime();
    if (hoveredTimestampMs === undefined) {
      return;
    }
    const hoveredTimestamp = hoveredTimestampMs / 1000;
    if (!$workspaceResourceTotals) {
      return;
    }
    const hoveredMetrics = $workspaceResourceTotals.metrics.filter(
      (metric) => metric.timestamp === hoveredTimestamp,
    );
    return getResourcesTotals(
      hoveredMetrics,
      $workspaceResourceTotals.timeBucketSecs,
    );
  }

  $: hoveredTotalResources = getHoveredTotalResources(
    processedData,
    hoveredIndex,
  );

  $: debouncedUpdate(
    `/api/workspaces/${workspace}/usage-by-workspace` +
      makeQueryParams({
        ...(timeRange
          ? { timestampStart: timeRange[0], timestampEnd: timeRange[1] }
          : { recent: duration }),
        bucket: Math.max(timeBucketSecs, 60), // metrics graphs don't render properly under 1-minute bucket sizes
        gapfill: 1,
        resource: selectedResource,
        limit: 5,
      }),
  );
  $: debouncedUpdateWorkspaceResourceTotals(
    `/api/workspaces/${workspace}/usage-by-resource` +
      makeQueryParams({
        ...(timeRange
          ? { timestampStart: timeRange[0], timestampEnd: timeRange[1] }
          : { recent: duration }),
        bucket: Math.max(timeBucketSecs, 60),
        gapfill: 1,
      }),
  );

  type ProcessedData = {
    date: Date;
  } & Omit<Record<string, number>, "date">;

  let appIds: string[] = [];
  let processedData: ProcessedData[];
  let averageUsage: Record<string, number> = {};
  let appInfoList: AppInfo[] = [];

  function scaleResource(value: number, timeBucketSecs: number) {
    return (value * 1e-9) / timeBucketSecs;
  }

  function getResourcesTotals(
    metrics: WorkspaceMetric[],
    timeBucketSecs: number,
  ): Pick<
    WorkspaceActiveResources,
    "activeGpus" | "activeContainers" | "gpuCounts"
  > {
    const {
      elapsed_ns,
      gpu_ns,
      cpu_ns: _cpu_ns,
      mem_ns: _mem_ns,
      ...groupedMetrics
    } = metrics.reduce(
      (acc, m) => {
        acc[m.resource] ??= 0;
        acc[m.resource] += m.value;
        return acc;
      },
      {} as Record<string, number>,
    );

    const gpuCounts = Object.entries(groupedMetrics).map(
      ([gpuType, value]) => ({
        numGpus: scaleResource(value, timeBucketSecs),
        gpuTypeStr: gpuType,
      }),
    );

    return {
      activeGpus: scaleResource(gpu_ns, timeBucketSecs),
      activeContainers: scaleResource(elapsed_ns, timeBucketSecs),
      gpuCounts,
    };
  }

  async function processData(data: WorkspaceMetricsResponse) {
    averageUsage = {};
    const workspaceMetrics = [...data.leftoverMetrics, ...data.metrics];
    const groupedData = workspaceMetrics.reduce(
      (acc, metric) => {
        if (!metric.timestamp) return acc; // TODO: Figure out why sometimes we get a timestamp of 0

        const resourceInfo = resourceOptions[metric.resource as Resource];
        if (!acc[metric.timestamp]) acc[metric.timestamp] = {};
        acc[metric.timestamp][metric.appId] =
          (metric.value / data.timeBucketSecs) * resourceInfo.multiplier;
        averageUsage[metric.appId] =
          (averageUsage[metric.appId] || 0) +
          (metric.value / duration) * resourceInfo.multiplier;
        return acc;
      },
      {} as Record<number, Record<string, number>>,
    );
    const results: ProcessedData[] = Object.entries(groupedData).map(
      ([timestamp, metrics]) =>
        ({
          date: new Date(1000 * Number(timestamp)),
          ...metrics,
        }) as ProcessedData,
    );
    appIds = Object.keys(averageUsage);
    processedData = results.sort((a, b) => a.date.getTime() - b.date.getTime());
    appInfoList = data.apps;
  }

  function onChangeDomainX({ detail }: { detail: [Date, Date] }) {
    const MIN_WINDOW = 60 * 2; // 2 minutes in seconds
    const startTime = Math.floor(detail[0].getTime() / 1000);
    const endTime = Math.floor(detail[1].getTime() / 1000);
    const timeDifference = endTime - startTime;
    if (timeDifference < MIN_WINDOW) return;

    start = startTime;
    end = endTime;
    live = false;
  }

  const handleSelectResource = (id: string) => {
    cachedWorkspaceMetrics = undefined;
    selectedResource = id as keyof typeof resourceOptions;
  };

  const getAppName = (appInfo: AppInfo | undefined) => {
    if (appInfo === undefined) return "Other";
    if (appInfo.name) return appInfo.name;
    if (appInfo.description) return appInfo.description;
    return appInfo.appId;
  };
  const getAppLink = (appInfo: AppInfo | undefined) => {
    if (appInfo === undefined) return undefined;
    if (appInfo.state === AppState.APP_STATE_DEPLOYED) {
      return `/apps/${workspace}/${encodeURIComponent(appInfo.environment)}/deployed/${encodeURIComponent(appInfo.name)}`;
    }
    return `/apps/${workspace}/${encodeURIComponent(appInfo.environment)}/${appInfo.appId}`;
  };

  $: gpuCounts = (hoveredTotalResources?.gpuCounts ?? resources.gpuCounts)
    .filter((g) => g.numGpus > 0)
    .toSorted((a, b) => a.gpuTypeStr.localeCompare(b.gpuTypeStr));
</script>

<div class="container h-full">
  <div class="flex items-center">
    <div class="flex gap-10">
      <div class="flex flex-col gap-1">
        <div class="text-white/40">GPUs</div>
        <div class="text-white/80 tabular-nums">
          {hoveredTotalResources?.activeGpus.toFixed(2) || resources.activeGpus}
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <div class="text-white/40">Containers</div>
        <div class="text-white/80 tabular-nums">
          {hoveredTotalResources?.activeContainers.toFixed(2) ||
            resources.activeContainers}
        </div>
      </div>
    </div>
    <div class="flex gap-10 ml-auto">
      {#each gpuCounts as gpuCount}
        <div class="flex flex-col gap-1">
          <div class="text-white/40">
            {gpuCount.gpuTypeStr}
          </div>
          <div class="text-white/80 tabular-nums">
            {hoveredTotalResources
              ? gpuCount.numGpus.toFixed(2)
              : gpuCount.numGpus}
          </div>
        </div>
      {/each}
    </div>
  </div>
  <div class="my-4">
    <Divider />
  </div>
  <div>
    <div class="flex flex-col sm:flex-row items-left sm:items-center gap-4">
      <div class="text-lg">
        App Breakdown — {resourceOptions[selectedResource].name}
      </div>

      <div class="ml-auto"></div>
      <DropdownSelect align="full" size="wide" value={selectedResource}>
        <button
          slot="button"
          class="btn btn-outlined py-1.5 gap-x-2 bg-white/5"
        >
          {#if selectedResource}
            <span class="text-white/60">Resource: </span>
            <span class="text-white">
              {resourceOptions[selectedResource].name}
            </span>
          {/if}
          <ChevronDown size={20} class="text-zinc-500" />
        </button>

        {#each Object.entries(resourceOptions) as [id, resource] (id)}
          <DropdownSelectItem
            title={resource.name}
            isActive={selectedResource === id}
            on:click={() => handleSelectResource(id)}
          />
        {/each}
      </DropdownSelect>
      <MetricsRangeDropdown
        {start}
        {end}
        {live}
        {duration}
        on:update={({ detail }) => {
          start = detail.start;
          end = detail.end;
          live = detail.live;
        }}
      />
    </div>
    <div class="mt-4">
      {#if cachedWorkspaceMetrics}
        {#if cachedWorkspaceMetrics.metrics.length}
          <MetricGraphContainer>
            <StackedTimeseriesChart
              variant="Area"
              title={""}
              {xDomain}
              {seriesColors}
              formatTooltip={() => ""}
              displayUtc={false}
              data={processedData}
              seriesNames={appIds}
              {selectIndex}
              tooltip={false}
              on:changeDomainX={onChangeDomainX}
              bind:hoveredIndex
            />
          </MetricGraphContainer>
        {/if}
        <MetricGraphNoData />
      {:else}
        <MetricGraphSkeleton />
      {/if}
    </div>

    <div class="mt-4 h-72 overflow-y-auto" style:scrollbar-gutter="stable">
      <MinimalTable>
        <thead class="sticky top-0 z-10 bg-zinc-800">
          <tr class="text-white/40">
            <th class="text-left w-12"></th>
            <th class="text-left font-normal w-60">App Name</th>
            <th class="text-left font-normal w-28"
              >{hoveredIndex ? "Value" : "Average"}</th
            >
            <th class="text-left font-normal w-60">Usage</th>
          </tr>
        </thead>
        <tbody>
          {#if cachedWorkspaceMetrics}
            {#each [...appIds].reverse() as id, i (id + i)}
              {@const index = appIds.length - 1 - i}
              {@const appInfo = appInfoList.find((info) => info.appId === id)}
              <tr
                class="mb-1 h-8 hover:bg-white/10 transition-colors cursor-pointer tabular-nums"
                on:mouseover={() => (selectIndex = index)}
                on:focus={() => (selectIndex = index)}
                on:mouseleave={() => (selectIndex = undefined)}
                on:click={() => {
                  const url = getAppLink(appInfo);
                  if (url) {
                    window.open(url, "_blank");
                  }
                }}
              >
                <td>
                  <div
                    style:background-color={seriesColors[index]}
                    class="w-4 h-4 rounded-sm"
                  ></div>
                </td>
                <td>
                  {getAppName(appInfo)}
                </td>
                {#if hoveredIndex && hoveredIndex < processedData.length}
                  <td>
                    {resourceOptions[selectedResource].formatLabel(
                      processedData[hoveredIndex][id],
                    )}
                  </td>
                {:else}
                  <td>
                    {resourceOptions[selectedResource].formatLabel(
                      averageUsage[id],
                    )}
                  </td>
                {/if}
                <td class="text-right h-10">
                  <Sparkline
                    {xDomain}
                    data={processedData.map((d) => ({
                      date: d.date,
                      value: d[id],
                    }))}
                    stroke={seriesColors[index]}
                    fill={seriesColors[index] + "0e"}
                  />
                </td>
              </tr>
            {/each}
          {:else}
            {#each { length: 6 } as _, i (i)}
              <tr>
                <td>
                  <div class="h-5 animate-pulse rounded bg-white/20"></div>
                </td>
                <td>
                  <div class="h-5 animate-pulse rounded bg-white/20"></div>
                </td>
                <td>
                  <div class="h-5 animate-pulse rounded bg-white/20"></div>
                </td>
                <td>
                  <div class="h-5 animate-pulse rounded bg-white/20"></div>
                </td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </MinimalTable>
    </div>
  </div>
</div>
